import type { Messages } from './index';

// English. Must mirror sv.ts exactly — TypeScript fails the build if a key
// is missing or extra.
const en: Messages = {
  appError: {
    title: 'Something went wrong',
    body: 'The page could not be displayed. The error has been reported automatically. Reload the page and try again.',
    reload: 'Reload page',
  },
  legal: {
    terms: 'Terms of use',
    privacy: 'Privacy policy',
    acceptLabel: 'I accept the {terms} and have read the {privacy}.',
    mustAccept: 'You need to accept the terms of use',
    comingSoon: 'The text will be published shortly. Questions in the meantime? Email info@qvestium.com.',
    back: '← Back to Qvestium',
    updatedTitle: 'We have updated our terms',
    updatedBody: 'Please read the {terms} and the {privacy}. To keep using Qvestium you need to accept them.',
    accept: 'I accept',
    loading: 'Loading…',
  },
  sellerType: {
    title: 'Seller',
    intro: 'Do you sell or rent out as a private person or as a business? It is shown to buyers, since consumer protection (the Consumer Sales Act and the right of withdrawal) only applies when the seller is a business. You need to set this before you list items.',
    private: 'Private person',
    business: 'Business',
    businessName: 'Business name',
    orgNumber: 'Company registration number',
    vatNumber: 'VAT number (optional)',
    address: 'Business address (shown to buyers)',
    save: 'Save',
    saved: 'Saved',
    notSet: 'Not set yet',
    buyerPrivate: 'Sold by a private person. The Consumer Sales Act and the right of withdrawal do not apply between private persons, but Qvestium buyer protection does.',
    buyerBusiness: 'Sold by the business {name}, reg. no. {org}, {address}. As a consumer you have the right of withdrawal and protection under the Consumer Sales Act against the seller.',
  },
  myData: {
    title: 'Your data',
    intro: 'Download everything we have stored about you, or delete your account.',
    download: 'Download my data',
    delete: 'Delete my account',
    deleteTitle: 'Delete account',
    deleteBody: 'Your profile, address, listings and images, likes, saved cards and connections are deleted. Messages and reviews that remain with others are shown as from "Deleted user". Reviews others gave you are kept without your name, and if you log in again with the same email they are linked back. If you signed in with Apple, Qvestium\'s connection to your Apple ID is revoked. Transactions and payments are kept for seven years as required by bookkeeping law. Your login (Qid), shared with other Q services, remains — contact info@qvestium.com if you want it deleted too.',
    typeToConfirm: 'Type RADERA to confirm',
    confirmDelete: 'Delete account permanently',
    cannotDelete: 'The account cannot be deleted yet: {reasons}. Contact info@qvestium.com if you have questions.',
    deleted: 'Your account has been deleted.',
  },
  taxInfo: {
    title: 'Tax information (DAC7)',
    intro: 'If you sell at least 30 items or for more than 2,000 euro (about 22,000 SEK) per year, Qvestium is required by law to report your sales to the Swedish Tax Agency. We then need your details. Your personal identity number is stored encrypted and never shown to others. Rentals are not covered.',
    requiredNotice: 'You are approaching the threshold — please submit your details by {date}.',
    heldNotice: 'Your payouts (sales and rentals) are paused until your details have been submitted.',
    submitted: 'Submitted {date}: {name}, {tin}.',
    update: 'Change details',
    individual: 'Private person',
    entity: 'Business',
    firstName: 'First name',
    lastName: 'Last name',
    personnummer: 'Personal identity number (YYYYMMDD-NNNN)',
    legalName: 'Business name',
    orgNumber: 'Company registration number',
    vatNumber: 'VAT number (optional)',
    address: 'Address',
    postalCode: 'Postal code',
    city: 'City',
    save: 'Save details',
    saved: 'Saved — thank you!',
    show: 'Submit details',
    adminTitle: 'DAC7 report data',
    adminIntro: 'Sellers to be reported to the Swedish Tax Agency (at least 30 sales or more than 2,000 euro), with counts and amounts per quarter. The report is due by 31 January the following year.',
    adminDownload: 'Download {year} (CSV)',
  },
  meta: {
    title: 'Qvestium — Fashion marketplace',
  },
  language: {
    label: 'Language',
  },
  common: {
    member: 'Qvestium member',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    saveChanges: 'Save changes',
    close: 'Close',
    loading: 'Loading...',
    back: 'Back',
    delete: 'Delete',
    edit: 'Edit',
    confirm: 'Confirm',
    send: 'Send',
    sending: 'Sending...',
    logIn: 'Log in',
    createAccount: 'Create account',
    upload: 'Upload',
    or: 'or',
    somethingWentWrong: 'Something went wrong',
    authRequired: 'Authentication required',
    kr: 'SEK {amount}',
    krPerMonth: 'SEK {amount}/mo',
    months: '{count} mo',
    buy: 'Buy',
    rent: 'Rent',
    swap: 'Swap',
    seller: 'Seller',
    buyer: 'Buyer',
    price: 'Price',
    size: 'Size',
    category: 'Category',
    description: 'Description',
    pay: 'Pay',
    reject: 'Decline',
    permanent: 'Permanent',
  },
  api: {
    appleLoginFailed: 'Apple sign-in failed',
    appleNoAccount: 'There is no Qvestium account for this Apple ID yet. Create an account with email and an invite code first (or sign in with Google), then you can sign in with Apple. If you chose "Hide My Email" with Apple we can\'t find your account – choose "Share My Email" instead.',
    noAccount: 'There is no Qvestium account for this account yet.',
    enterInviteCode: 'Enter your invite code',
    googleLoginFailed: 'Google sign-in failed',
    googleNoAccountInApp: 'There is no Qvestium account for this Google account yet. Create one with email and an invite code here in the app, or with Google on qvestium.com.',
    popupBlocked: 'The sign-in window was blocked by the browser',
    popupClosed: 'The sign-in window was closed before sign-in finished',
    stateMismatch: 'The sign-in could not be verified — please try again',
    tokenExchangeFailed: 'Sign-in failed',
    fetchUserFailed: 'Could not load your account details',
    loginFailed: 'Sign-in failed',
    registrationFailed: 'Registration failed',
    wrongCode: 'Incorrect code',
    resendCodeFailed: 'Could not send a new code',
    newCodeSent: 'A new code has been sent',
    insuranceQuoteFailed: 'Could not get an insurance quote',
    requestNotFound: 'Request not found',
    paymentStartFailed: 'Could not start the payment',
    verificationStartFailed: 'Could not start the verification',
    shipmentCheckFailed: 'Could not check the shipment',
    paymentSessionFailed: 'Could not create a payment session',
    uploadFailed: 'Upload failed',
    aiImageFailed: 'AI image generation failed',
    tryOnFailed: 'Could not try on the item',
    qUnavailable: 'Q could not answer right now',
    vintedImportFailed: 'Could not start the Vinted import',
    importStatusFailed: 'Could not get the import status',
    analyzeFailed: 'Could not analyse the item',
  },
  featured: {
    badge: 'Featured',
    title: 'Featured',
    explainer: 'Featured listings are shown at the top of the marketplace.',
    byQvestium: 'Picked by Qvestium — shown at the top of the marketplace.',
    until: 'Shown at the top of the marketplace until {date}.',
    notListed: 'List the item for sale, rent or swap to make it featured.',
    featureWeek: 'Feature for one week',
    extendWeek: 'Extend by one week',
    creditsLeft: { one: 'You have {count} featured week left', other: 'You have {count} featured weeks left' },
    buyPack: 'Buy featured — {items} items for SEK {price}',
    packInfo: 'A pack gives you {items} items featured for one week each (SEK {perWeek} per item and week). This item is featured right away, and you can use the other {rest} weeks on other items whenever you like.',
    adminFeature: 'Make featured (admin)',
    adminRemove: 'Remove featured (admin)',
    confirmRemovePaid: 'The owner has paid for this week. Remove featured anyway?',
  },
  // ── Komponenter (genererat) ──
  favoriteHeartButton: {
    removeSaved: 'Remove from saved',
    save: 'Save',
  },
  aiImageBadge: {
    label: 'AI image',
  },
  wardrobeLikeButton: {
    unlike: 'Unlike',
    like: 'Like',
  },
  ownerLikesBadge: {
    likes: { one: '{count} like', other: '{count} likes' },
  },
  likersModal: {
    loadError: 'Couldn\'t load likes',
    title: 'Liked by',
    empty: 'No one has liked this item yet',
  },
  loginModal: {
    forgotPassword: 'Forgot your password?',
    forgotTitle: 'New password',
    forgotIntro: "Enter the email address you sign in with and we'll email you a code to choose a new password.",
    sendResetCode: 'Send code',
    newPasswordLabel: 'New password (at least 8 characters)',
    saveNewPassword: 'Save and sign in',
    passwordMismatch: 'Passwords don\'t match',
    inviteCodeRequired: 'Enter your invite code',
    wrongCode: 'Incorrect code',
    resendFailed: 'Couldn\'t send a new code',
    loginCancelled: 'Sign-in was cancelled',
    popupBlocked: 'Your browser blocked the Google window. Click the button again — if that doesn\'t help, allow pop-ups for qvestium.com.',
    googleLoginFailed: 'Couldn\'t sign in with Google',
    wrongInviteCode: 'Incorrect invite code',
    createAccountFailed: 'Couldn\'t create the account',
    confirmEmailTitle: 'Confirm your email',
    codeSentDefault: 'We\'ve sent a six-digit code to your email.',
    codeSentTo: 'The code was sent to {email}.',
    codeLabel: 'Code from the email',
    resendCode: 'Send a new code',
    googleInviteIntro: 'You don\'t have a Qvestium account yet. Enter your invite code to create one with your Google account.',
    inviteCodeLabel: 'Invite code',
    continueWithGoogle: 'Continue with Google',
    continueWithApple: 'Continue with Apple',
    appleLoginFailed: 'Couldn\'t sign in with Apple',
    orWithEmail: 'or with email',
    emailLabel: 'Email',
    emailPlaceholder: 'you@email.com',
    passwordLabel: 'Password',
    confirmPasswordLabel: 'Confirm password',
  },
  collectionCard: {
    featured: 'Featured',
    spotsLeft: '{count} left',
    orMonthly: 'or SEK {amount}/mo',
    seeMore: 'See more',
  },
  marketplaceWardrobeCard: {
    sizeShort: 'Size {size}',
    buyPrice: 'SEK {amount} to buy',
    orBuyPrice: 'or SEK {amount} to buy',
    openForSwap: 'Open to swaps',
    rent: 'Rent',
    buyFor: 'Buy SEK {amount}',
    inclBuyerProtection: 'SEK {amount} incl. Buyer Protection',
  },
  shopCard: {
    soldOut: 'Sold out',
    draft: 'Draft',
    sizeShort: 'Size {size}',
    orBuyPrice: 'or {amount} {unit} to buy',
    rent: 'Rent',
  },
  conditionLabels: {
    worn: 'Worn',
    used: 'Used',
    good: 'Good',
    veryGood: 'Very good',
    unused: 'Unused',
  },
  weightTierOptions: {
    light: 'Light (under 1 kg) — SEK {amount} shipping',
    medium: 'Medium (1–2 kg) — SEK {amount} shipping',
    heavy: 'Heavy (2–3 kg) — SEK {amount} shipping',
  },
  sortOptions: {
    newest: 'Newest',
    priceAsc: 'Price: Low to high',
    priceDesc: 'Price: High to low',
  },
  marketplaceFilterBar: {
    sortBy: 'Sort by',
    sortByValue: 'Sort by: {option}',
    condition: 'Condition',
    sizes: 'Sizes',
    noSizes: 'No sizes to filter by',
    minPrice: 'Min (SEK)',
    maxPrice: 'Max (SEK)',
    clearFilters: 'Clear filters',
  },
  tryOnButton: {
    generateFailed: 'Couldn\'t generate the image',
    generating: 'Creating an image of you in this item... (may take ~30s)',
    tryOn: 'Try it on me',
    noPhoto: 'Upload a full-body photo in {link} to try items on yourself.',
    yourProfile: 'your profile',
    pickPhoto: 'Choose a photo:',
    resultTitle: 'Here\'s how it could look on you',
    aiDisclaimer: 'AI-generated image — a visual approximation, not an exact fit.',
  },
  outfits: {
    title: 'Outfits with Q',
    intro: 'Describe a style, a reference or an occasion – or add a picture. Q combines items from your wardrobe with items to buy or rent.',
    placeholder: 'E.g. "old money", "Sofia Coppola in the 90s" or "festive but relaxed, wedding in August"',
    addImage: 'Add picture',
    removeImage: 'Remove picture',
    submit: 'Suggest outfits',
    working: 'Q is putting outfits together…',
    failed: "Couldn't put outfits together right now.",
    imageUploadFailed: "The picture couldn't be uploaded.",
    interpreted: 'How I read it',
    none: "Couldn't find combinations that work together. Try another description or add more items to your wardrobe.",
    own: 'Yours',
    buy: 'Buy',
    rent: 'Rent',
    priceBuy: '{amount} kr',
    priceRent: '{amount} kr/mo',
    totalBuy: 'Buy {amount} kr',
    totalRent: 'Rent {amount} kr/mo',
    totalOwnOnly: 'Everything is in your wardrobe',
    tryOn: 'Try on me',
    tryingOn: 'Creating a picture of you… (may take ~30 s)',
    tryOnFailed: "Couldn't create the picture.",
    noBodyPhoto: 'Upload a full-body photo in {link} to see the outfit on you.',
    yourProfile: 'your profile',
    showCollage: 'Show items',
    showOnMe: 'Show on me',
    aiDisclaimer: 'AI-generated picture only you can see. An approximation, not an exact fit.',
  },
  productDetailPage: {
    priceOnce: 'SEK {amount} one-time',
    size: 'Size {size}',
    choosePayment: 'Choose how to pay',
    toPay: 'To pay',
    bindingInfo: { one: 'Commitment period {count} month · First month is paid at the start', other: 'Commitment period {count} months · First month is paid at the start' },
    buyFor: 'Buy for SEK {amount}',
    rentNow: 'Rent now · {price}',
  },
  collectionDetailModal: {
    logInToBook: 'Log in to book this collection.',
    featured: 'FEATURED',
    designBy: 'Designed by {designer}',
    highlights: 'Highlights',
    includes: 'Included',
    availability: 'Availability',
    spotsLeft: '{available} of {total} left',
    orMonthly: 'or SEK {amount}/month for 12 months',
    yourDetails: 'Your details',
    nameLabel: 'Name *',
    namePlaceholder: 'Your name',
    emailLabel: 'Email *',
    emailPlaceholder: 'you@email.com',
    phoneLabel: 'Phone',
    phonePlaceholder: '+46 70 xxx xx xx',
    messageLabel: 'Message',
    messagePlaceholder: 'Requests, questions...',
    confirmTitle: 'Confirm order',
    summaryCollection: 'Collection: {value}',
    summaryName: 'Name: {value}',
    summaryEmail: 'Email: {value}',
    summarySize: 'Size: {value}',
    summaryPrice: 'Purchase price: {value}',
    summaryMonthly: 'Monthly price: {value} (12 months)',
    orderSent: 'Order sent!',
    weWillReply: 'We\'ll get back to you at {email} with a confirmation.',
    rentNow: 'Rent now',
    orderNow: 'Order now',
    continue: 'Continue',
  },
  bindingOptions: {
    flexible: 'Flexible',
    popular: 'Popular',
    ownIt: 'Own it',
  },
  crm: {
    intro: 'Profiles on other second-hand sites we want to bring to Qvestium. Drag the cards between stages. Every profile has a personal invite code — when they register with it, the card moves automatically.',
    funnel: '{total} profiles · {contacted} contacted · {converted} registered · {active} have listed',
    loadFailed: "Couldn't load the CRM",
    add: 'Add profile',
    import: 'Import list',
    search: 'Search name, city, style …',
    allPlatforms: 'All sites',
    allOwners: 'All owners',
    allPriorities: 'All priorities',
    unnamed: 'Unnamed profile',
    notesCount: '{count} notes',
    profileUrl: 'Profile URL',
    or: 'or',
    platform: 'Site',
    handle: 'Username',
    displayName: 'Name',
    owner: 'Owner',
    priority: 'Priority',
    stage: 'Stage',
    contactEmail: 'Email',
    contactPhone: 'Phone',
    location: 'City',
    categories: 'Style / categories',
    audience: 'Followers / listings',
    nextStep: 'Next step',
    nextStepDate: 'Date',
    description: 'Notes about the profile',
    save: 'Save',
    importHint: 'Paste profile URLs, one per line. The site and username are detected automatically, and profiles already in the CRM are skipped.',
    preview: 'Preview',
    importCount: 'Import {count}',
    willImport: 'Will be added',
    openProfile: 'Open profile',
    registeredAs: 'registered as {email}',
    inviteTitle: 'Personal invite · code {code}',
    messageTemplate: 'Hej {name}! Vi har sett din garderob på {platform} och tror att den skulle passa perfekt på Qvestium — en ny svensk marknadsplats där du kan sälja, hyra ut och byta kläder. Du betalar ingen provision när du säljer.\n\nVi vill gärna bjuda in dig. Registrera dig med din personliga kod {code} här:\n{link}\n\nHälsningar\n{owner}, Qvestium',
    copyMessage: 'Copy message',
    copied: 'Copied!',
    noteKinds: { dm: 'DM', email: 'Email', call: 'Call', note: 'Note' },
    notePlaceholder: 'What happened? (a DM or email moves a new profile to Contacted)',
    addNote: 'Add',
    gdprHint: "Delete profiles that aren't interested — we keep data about people who aren't users.",
    delete: 'Delete profile',
    confirmDelete: 'Delete the profile and all notes?',
    stages: { identified: 'Identified', contacted: 'Contacted', replied: 'Replied', registered: 'Registered', active: 'Active', lost: 'Not interested' },
    platforms: { vinted: 'Vinted', tradera: 'Tradera', sellpy: 'Sellpy', plick: 'Plick', blocket: 'Blocket', instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', other: 'Other' },
  },
  shipping: {
    shipping: 'Shipping',
    loading: 'Getting shipping prices …',
    loadFailed: "Couldn't get shipping prices",
    none: 'No shipping options were found for your address.',
    toAgent: 'To a service point',
    toDoor: 'To your door',
    nearestAgent: 'Nearest service point: {agent}',
    inclVat: 'Prices are valid today and include VAT. Qvestium books the shipping once the order is paid.',
    choose: 'Choose shipping',
    chooseAndPay: 'Choose shipping and pay',
    payAmount: 'Pay {amount} kr',
    returnTitle: 'Shipping to return {name}',
    swapTitle: 'Shipping for your items in the swap',
    openLabel: 'Open the label',
    openLabelN: 'Label {n}',
    labelReadyInfo: 'Shipping is booked and paid. Print the label or show it at the service point, and hand in the parcel.',
    labelPending: 'Shipping is booked. The label will appear here within a few minutes, and you will get an email.',
    dropOffAt: 'Hand in at: {agent}',
    pickUpAt: 'Pick up at: {agent}',
    trackingNumber: 'Tracking number: {number}',
    track: 'Track the parcel',
    status: {
      booking: 'Booking shipping …',
      bookingFailed: "Booking the shipping is taking longer than usual. We've been alerted and will sort it out — you don't need to do anything.",
      booked: 'Booked',
      shipped: 'Shipped',
      in_transit: 'On its way',
      delivered: 'Delivered',
      exception: 'Needs checking',
      cancelled: 'Cancelled',
    },
  },
  cartModal: {
    paymentServerUnreachable: 'Couldn\'t reach the payment server',
    title: 'Your bag ({count})',
    confirmTitle: 'Confirm order',
    processing: 'Processing...',
    empty: 'Your bag is empty',
    creatingPayment: 'Creating payment...',
    redirectingToStripe: 'You\'ll be sent to Stripe for secure payment',
    reviewOrder: 'Review your order before paying:',
    rentMonths: { one: 'Rent for {count} month', other: 'Rent for {count} months' },
    payNow: 'Due now',
    monthlyAfter: 'Monthly thereafter',
    secureStripe: 'Secure payment with Stripe',
    stripeInfo: 'Your card details are handled securely by Stripe. We never store your card information.',
    payAmount: 'Pay SEK {amount}',
    uniqueItem: 'One-of-a-kind item',
    buyNoBinding: 'Buy — one-time price, no commitment period',
    delivery: 'Delivery:',
    pickup: 'Pickup',
    shippingCost: 'Shipping (SEK {amount})',
    insuranceName: 'Rental insurance',
    insuranceVia: '{name} via Omocom',
    fetchingPrice: 'fetching price…',
    insuranceQuote: { one: 'SEK {premium} for {count} month, covers up to SEK {insured}', other: 'SEK {premium} for {count} months, covers up to SEK {insured}' },
    insuranceInfo: 'Covers damage and theft during the rental, so you don\'t have to cover the full value yourself.',
    buyOneTime: 'Buy (one-time price)',
    rentLabel: 'Rent',
    rentFirstMonth: '(first month)',
    shipping: 'Shipping',
    insuranceNote: '(Omocom, entire rental period)',
    buyerProtection: 'Buyer Protection',
    buyerProtectionNote: '(your money is held until you\'ve received the item)',
    termsNote: 'The first month is paid now. After that the rent is charged monthly to your card for the whole commitment period, counted from the day the item is delivered. When the period ends you return the item.',
    fetchingInsurancePrice: 'Fetching insurance price…',
    goToCheckout: 'Go to checkout',
  },
  copyField: {
    copied: 'Copied',
    copy: 'Copy',
  },
  oAuthConsentModal: {
    title: 'Connect to Qvestium',
    description: '{client} wants to act as you on Qvestium: search for items, manage your listings, make offers and propose swaps, read and send messages, and handle returns. You always make payments yourself.',
    redirectNotice: 'The response will be sent to {host}. Only approve if you just tried to connect yourself. You can revoke access under Profile → Agents (MCP).',
    deny: 'Deny',
    allow: 'Allow',
    logInToContinue: 'Log in to continue',
    loggedInAs: 'Logged in as {email}',
  },
  pushPrompt: {
    title: 'Get notifications from Qvestium',
    body: 'We\'ll let you know when you get a message or an offer, when someone buys or rents your item, and when something happens in your deals.',
    enable: 'Turn on notifications',
    notNow: 'Not now',
  },
  pushSettings: {
    title: 'Notifications',
    appOnly: 'Notifications are sent to the Qvestium app on iPhone. These settings apply there.',
    deniedHint: 'Notifications are turned off for Qvestium in your phone\'s settings (Settings → Notifications → Qvestium).',
    typeMessages: 'Messages',
    typeDeals: 'Deals – offers, purchases, shipping, swaps and payouts',
    typeRentals: 'Rentals and returns',
    typeSupport: 'Replies from customer support',
    quietHours: 'Quiet hours',
    essentialNote: 'Reminders to confirm receipt, return a rented item or pay a rent are always sent.',
    saved: 'Saved',
  },
  agentsMcpCard: {
    tokenPlaceholder: '<your-token>',
    createFailed: 'Couldn\'t create token',
    revokeConfirm: 'Revoke "{name}"? Agents using it will lose access immediately.',
    revokeFailed: 'Couldn\'t revoke',
    hints: {
      claudeAi: 'In Claude (web or app): Settings → Connectors → Add custom connector. Paste the address, click Connect and approve on Qvestium. No token needed — the connection appears under Active tokens and can be revoked there.',
      claudeCode: 'Run this in your terminal. Then start Claude Code and ask, for example, "find a wool coat in size M under SEK 800".',
      claudeDesktop: 'Add to claude_desktop_config.json (Settings → Developer → Edit config) and restart Claude. Requires Node.js.',
      generic: 'For clients that support MCP over HTTP (e.g. Cursor and VS Code). The file name and key may differ between clients.',
    },
    title: 'Agents (MCP)',
    intro: 'Let an AI agent, such as Claude, use Qvestium for you via {mcp} (Model Context Protocol). The agent can search for items, create and edit your listings, make offers, propose swaps, reply to messages and handle returns — as you, with the same rules as here. You always make payments yourself: the agent gives you a payment link. Agents never get admin permissions.',
    claudeAiNote: 'With {claudeAi}, the server address below is all you need — you log in and approve directly. Other clients need an access token.',
    step1: '1. Create an access token (not needed for Claude.ai)',
    copyNow: 'Copy the token now — it\'s only shown once. Treat it like a password.',
    done: 'Done',
    namePlaceholder: 'Name, e.g. Claude',
    createToken: 'Create token',
    step2: '2. Connect your agent',
    serverAddress: 'Server address:',
    otherClients: 'Other clients',
    activeTokens: 'Active tokens',
    noneYet: 'None yet.',
    tokenMetaUsed: 'Created {created} · last used {used}',
    tokenMetaNeverUsed: 'Created {created} · never used',
    revoke: 'Revoke',
  },
  profileView: {
    shippingNamePlaceholder: 'Name on parcels (first and last name)',
    phonePlaceholder: 'Mobile number',
    shippingDetailsHint: 'Your name, address and mobile number are used on shipping labels and for delivery notices when you buy or sell with shipping. Not shown to others.',
    identity: {
      verified: 'Done — you\'re verified with BankID.',
      test: 'Test verification complete (Idura test environment). It doesn\'t count as a real verification.',
      cancelled: 'Verification was cancelled.',
      failed: 'Verification failed. Please try again.',
      expired: 'Verification timed out. Please try again.',
      startFailed: 'Couldn\'t start verification',
      verifiedBadge: '✓ Verified with BankID',
      verifiedWithName: '✓ Verified with BankID — {name}',
      testVerified: 'Test-verified in Idura\'s test environment. A real verification will be done with your regular BankID once the service is live.',
      testVerifiedWithName: 'Test-verified ({name}) in Idura\'s test environment. A real verification will be done with your regular BankID once the service is live.',
      intro: 'Verify yourself once with BankID. It makes Qvestium safer for everyone and is required to rent items. We don\'t store your personal identity number.',
      verifyButton: 'Verify with BankID',
    },
    connectStartFailed: 'Couldn\'t start the connection — please try again shortly',
    avatarUploadFailed: 'Couldn\'t upload the profile picture',
    bodyPhotosUploadFailed: 'Couldn\'t upload the photos',
    bodyPhotoRemoveFailed: 'Couldn\'t remove the photo',
    uploadAvatar: 'Upload profile picture',
    displayNamePlaceholder: 'Display name',
    editProfile: 'Edit profile',
    logOut: 'Log out',
    measurementsTitle: 'Measurements & Size',
    clothingSize: 'Clothing size',
    bustCm: 'Bust (cm)',
    waistCm: 'Waist (cm)',
    hipCm: 'Hips (cm)',
    cmValue: '{value} cm',
    stylePrefsTitle: 'Style preferences',
    noStylePrefs: 'No preferences saved',
    favoriteBrandsTitle: 'Favourite brands',
    addBrandPlaceholder: 'Add a brand...',
    noBrands: 'No brands saved',
    aboutMeTitle: 'About me',
    bioPlaceholder: 'Tell us about your style...',
    noBio: 'No description yet',
    addressTitle: 'Address',
    streetPlaceholder: 'Street address',
    postalCodePlaceholder: 'Postcode',
    cityPlaceholder: 'Town/city',
    countryPlaceholder: 'Country',
    areaCaption: 'Other users only see the approximate area within the circle, never your address.',
    addressNotOnMap: 'We couldn\'t show your address on the map — check your postcode and town.',
    noAddress: 'No address saved',
    identityTitle: 'Identity',
    starting: 'Starting...',
    payoutTitle: 'Payout account',
    payoutReady: '✓ All set — you can receive payouts automatically',
    payoutIncomplete: 'Your setup isn\'t finished yet — continue your registration with Stripe to receive automatic payouts once your sold or rented items are confirmed delivered.',
    payoutIntro: 'Connect a payout account to get paid automatically once your sold or rented items are confirmed delivered.',
    continueConnect: 'Continue setup',
    connectPayout: 'Connect payout account',
    bodyPhotosTitle: 'Full-body photos',
    uploadBodyPhoto: '+ Upload photo',
    bodyPhotosIntro: 'Upload full-body photos of yourself — they can be used as the model when AI generates "on model" images of your items, instead of a generic AI model.',
    noBodyPhotos: 'No photos uploaded yet',
    saveProfile: 'Save profile',
    adminBadge: 'ADMIN',
  },
  erbjudPlaggView: {
    imageUploadFailed: 'Image upload failed',
    mustBeLoggedIn: 'You need to be logged in',
    fillRequired: 'Please fill in all required fields',
    loginTitle: 'Log in to offer items',
    loginSubtitle: 'You need a Qid account to publish collections',
    interestedTitle: 'Interested in selling?',
    interestedBody: 'Contact us at {email} to become an approved seller on Qvestium.',
    title: 'Publish collection',
    fields: {
      title: 'Title *',
      subtitle: 'Subtitle',
      location: 'Location *',
      region: 'Region',
      brand: 'Brand',
      designer: 'Designer',
      style: 'Style',
      season: 'Season',
      price: 'Price *',
      priceUnit: 'Price unit',
      spotsTotal: 'Number of items available',
      contactEmail: 'Contact email',
      image: 'Image URL or upload',
      highlights: 'Highlights (max 3)',
      includes: 'What\'s included (max 3)',
    },
    placeholders: {
      title: 'Collection name',
      subtitle: 'Short tagline',
      description: 'Describe the collection...',
      location: 'Stockholm, Sweden',
      region: 'Scandinavia',
      brand: 'Your brand',
      designer: 'Designer\'s name',
      season: 'SPRING/SUMMER 2025',
      contactEmail: 'contact@email.com',
      highlight: 'Highlight {n}',
      include: 'Included {n}',
    },
    priceUnits: {
      perItem: 'SEK/item',
      perSet: 'SEK/set',
      perDress: 'SEK/dress',
      perSuit: 'SEK/suit',
    },
    featured: 'Mark as featured collection',
    published: 'Collection published!',
    publishing: 'Publishing...',
  },
  adminView: {
    statsLoadFailed: 'Couldn\'t load statistics',
    check: {
      noUpdate: 'No status update from Bring yet',
      alreadyUpdated: 'Already updated by an earlier check',
      newStatus: 'New status: {status}',
      unknown: 'unknown',
      failed: 'Couldn\'t check the shipment',
    },
    txLoadFailed: 'Couldn\'t load transactions',
    csv: {
      filename: 'qvestium-accounting-{from}-{to}.csv',
      all: 'all',
      now: 'now',
      date: 'Date',
      type: 'Type',
      status: 'Status',
      item: 'Item',
      seller: 'Seller',
      buyer: 'Buyer',
      price: 'Price (SEK)',
      commission: 'Commission (SEK)',
      protection: 'Buyer Protection (SEK, incl. VAT)',
      shipping: 'Shipping (SEK)',
      payout: 'Payout (SEK)',
    },
    typeBuy: 'Purchase',
    typeRental: 'Rental',
    tabs: {
      dashboard: 'Dashboard',
      operations: 'Operations',
      tickets: 'Tickets',
      ticketsCount: 'Tickets ({count})',
      transactions: 'Transactions',
      crm: 'CRM',
      marketing: 'Marketing',
      accounting: 'Accounting',
      offerItems: 'Offer items',
    },
    title: 'Admin',
    subtitle: 'Commercial overview, transactions and accounting',
    loadingStats: 'Loading statistics...',
    kpi: {
      users: 'Registered users',
      activeListings: 'Active listings',
      activeRentals: 'Ongoing rentals',
      totalOrders: 'Total orders',
      pendingShipments: 'Awaiting delivery',
      gmv: 'GMV (gross merchandise value)',
      revenue: 'Qvestium revenue',
      ofWhichProtection: 'of which Buyer Protection',
      sellerPayouts: 'Paid out to sellers',
    },
    accountingIntro: 'Choose a date range and export the transactions as CSV for bookkeeping and accounting.',
    from: 'From',
    to: 'To',
    allStatuses: 'All statuses',
    fetching: 'Fetching...',
    refresh: 'Refresh',
    exportCsv: 'Export CSV ({count})',
    totals: {
      revenue: 'Total turnover: {amount}',
      commission: 'Total commission: {amount}',
      protection: 'Total Buyer Protection: {amount}',
      payouts: 'Total paid out: {amount}',
    },
    noTransactions: 'No transactions match the selected filters',
    table: {
      date: 'Date',
      item: 'Item',
      type: 'Type',
      status: 'Status',
      commission: 'Commission',
      payout: 'Payout',
    },
    checking: 'Checking...',
    checkNow: 'Check now',
  },
  offPlatformWarning: {
    text: 'This message seems to be about paying outside Qvestium. Never pay off the platform — Buyer Protection won\'t apply and we can\'t help you if something goes wrong.',
  },
  chatItemCard: {
    viewListing: 'View listing',
  },
  messagesView: {
    translate: 'Translate',
    translating: 'Translating…',
    showOriginal: 'Show original',
    showTranslation: 'Show translation',
    translatedNote: 'Automatically translated',
    translateFailed: 'Could not translate the message',
    loadConversationsFailed: 'Couldn\'t load messages',
    interestedPrefill: 'Hi! I\'m interested in {item}. ',
    loadConversationFailed: 'Couldn\'t load the conversation',
    sendFailed: 'The message couldn\'t be sent',
    confirmBlock: 'Block {name}? You won\'t be able to message each other.',
    actionFailed: 'The action failed',
    empty: 'No messages yet. Open a listing and choose {action} to start a conversation.',
    emptyAction: 'Message the seller',
    youPrefix: 'You: {body}',
    regarding: 'Re: {item}',
    unblock: 'Unblock',
    block: 'Block',
    keepOnPlatform: 'Keep the deal on Qvestium — only pay through checkout so Buyer Protection applies.',
    report: 'Report',
    firstMessage: 'Write your first message to {name}.',
    youBlocked: 'You\'ve blocked {name}. Unblock them to send a message.',
    inputPlaceholder: 'Write a message…',
    title: 'Messages',
    selectConversation: 'Select a conversation.',
    reportTargetName: 'message from {name}',
  },
  supportTicketModal: {
    loadFailed: 'Couldn\'t load the form',
    chooseCategory: 'Please choose a category',
    submitFailed: 'Something went wrong — please try again',
    titleReportListing: 'Report listing',
    titleReportIllegal: 'Report illegal content',
    titleContact: 'Contact us',
    thanks: 'Thank you! Your case number is {number}.',
    confirmationReport: 'We\'ve sent a confirmation to {email}. A member of our team will review the report and let you know the decision.',
    confirmationSupport: 'We\'ve sent a confirmation to {email}. We\'ll reply as soon as we can, usually within one working day.',
    regarding: 'Regarding: {name}',
    reportIntro: 'Describe what\'s wrong and why, and we\'ll review it. The information is only used to handle the report.',
    choose: 'Choose…',
    whatsWrong: 'What\'s wrong?',
    howCanWeHelp: 'How can we help?',
    yourEmail: 'Your email (so we can reply)',
    nameOptional: 'Name (optional)',
    sendReport: 'Send report',
  },
  ticketStatus: {
    open: 'New',
    inProgress: 'In progress',
    closed: 'Closed',
  },
  adminTicketsView: {
    agent: {
      title: 'Agent draft',
      hasDraft: 'The agent has drafted a reply',
      working: 'The agent is drafting a reply …',
      failed: 'The agent couldn\'t draft a reply: {error}',
      retry: 'Run again',
      use: 'Use draft',
      inUse: 'The draft is in the reply box',
      useHint: 'Read it through and edit if needed, then press Send reply.',
      reject: 'Reject',
      rejected: 'The draft was rejected.',
      requeued: 'The agent is writing a new draft.',
      reasoning: 'Agent\'s reasoning: {text}',
      notApprover: 'Only Agnes and Lars can approve the agent\'s drafts.',
      decided: {
        approved: 'The agent\'s draft was sent unchanged by {by}.',
        edited: 'The agent\'s draft was sent with edits by {by}.',
        rejected: 'The agent\'s draft was rejected by {by}.',
      },
      actions: {
        none: 'Nothing more needed',
        refund: 'Suggests a refund',
        contact_seller: 'Suggests contacting the other party',
        escalate: 'Unsure — review carefully',
        close: 'Suggests closing the ticket',
      },
    },
    loadFailed: 'Couldn\'t load tickets',
    actionFailed: 'The action failed',
    noteSaved: 'Note saved.',
    replySent: 'Reply sent to {email}.',
    promptReportDecision: 'Decision — sent to the person who reported it (e.g. "The listing has been removed" or "The listing doesn\'t violate our terms"):',
    promptClosingMessage: 'Closing message to the user (leave empty to close without sending an email):',
    ticketClosed: 'Ticket closed.',
    promptRemoveListing: 'Remove the listing "{name}"?\n\nThe reason will be sent to the listing\'s owner:',
    removedInternalNote: 'Listing removed. Reason given to the owner: {reason}',
    listingRemovedNotice: 'The listing has been removed and the owner has been emailed. Close the ticket to notify the reporter of the decision.',
    removeFailed: 'Couldn\'t remove the listing',
    filters: {
      active: 'To handle',
      closed: 'Closed',
      all: 'All',
    },
    empty: 'No tickets here.',
    kinds: {
      report: 'Report',
      dispute: 'Dispute',
      support: 'Support',
    },
    selectTicket: 'Select a ticket.',
    reportFrom: 'Report from {reporter}',
    supportFrom: 'Support ticket from {reporter}',
    loggedIn: '(signed in)',
    notLoggedIn: '(not signed in)',
    concernsListing: 'Concerns the listing {name}',
    concernsUser: 'Concerns the user {name}',
    targetStatus: '(status: {status})',
    statusRemoved: 'removed',
    removeListing: 'Remove listing',
    internalNote: 'Internal note',
    replyToUser: 'Reply to user',
    internalNotePlaceholder: 'Internal note (visible to admins only)',
    replyPlaceholder: 'Reply to {email}',
    internalNoteCheckbox: 'Internal note (not sent)',
    saveNote: 'Save note',
    sendReply: 'Send reply',
    markedInProgress: 'Marked as in progress.',
    markInProgress: 'Mark in progress',
    closeWithDecision: 'Close with decision…',
    closeTicket: 'Close ticket…',
    decision: 'Decision: {resolution}',
    reopened: 'Ticket reopened.',
    reopen: 'Reopen',
  },
  issueLabels: {
    stuckPayment: 'Payment stuck',
    notShipped: 'Not shipped',
    notHandedOver: 'Not handed over',
    cancelDispute: 'Cancellation dispute',
    notDelivered: 'Not delivered',
    payoutFailed: 'Payout failed',
    payoutWaiting: 'Payout pending',
  },
  adminActionLabels: {
    removeListing: 'Removed listing',
    restoreListing: 'Restored listing',
    blockUser: 'Blocked account',
    unblockUser: 'Unblocked account',
    cancelTransaction: 'Cancelled transaction',
    retryPayout: 'Retried payout',
  },
  adminDriftView: {
    loadFailed: 'Couldn\'t load operations data',
    actionFailed: 'The action failed',
    reasonPrompt: '{question}\n\nThe reason will be sent to the user and saved in the log:',
    cancelPaidQuestion: 'Cancel the transaction for "{name}" and refund SEK {amount} to the buyer?',
    cancelUnpaidQuestion: 'Cancel the unpaid transaction for "{name}"?',
    removeListingQuestion: 'Remove the listing "{name}" ({userId})?',
    listingRemoved: '"{name}" has been removed and the owner has been emailed.',
    blockConfirm: 'Block {userId}? All their listings will be hidden and pending offers cancelled.',
    userBlocked: '{userId} has been blocked.',
    searchFailed: 'Search failed',
    needsAction: 'Needs action',
    needsActionCount: 'Needs action ({count})',
    noIssues: 'Nothing to handle right now.',
    unknownItem: 'Unknown item',
    parties: 'Seller {seller} · Buyer {buyer} · {id}',
    cancelAndRefund: 'Cancel and refund',
    retryPayout: 'Retry payout',
    sellerIsRight: 'Seller is right – pay out',
    moderation: 'Listing moderation',
    searchPlaceholder: 'Search by item name, email or ID',
    showRemoved: 'Show removed',
    searching: 'Searching...',
    search: 'Search',
    noResults: 'No results.',
    removedReason: 'Removed: {reason}',
    listingRestored: '"{name}" has been restored.',
    restore: 'Restore',
    blockOwner: 'Block owner…',
    blockedAccounts: 'Blocked accounts ({count})',
    emailPlaceholder: 'Email address',
    reasonPlaceholder: 'Reason (sent to the user)',
    block: 'Block',
    blockedMeta: '{reason} · {date} by {admin}',
    unblockConfirm: 'Unblock {userId}?',
    unblocked: '{userId} has been unblocked.',
    unblock: 'Unblock',
    recentActions: 'Recent actions',
    noActions: 'No actions yet.',
  },
  editCollectionModal: {
    imageUploadFailed: 'Image upload failed',
    title: 'Edit collection',
    fields: {
      title: 'Title *',
      subtitle: 'Subtitle',
      location: 'Location',
      region: 'Region',
      brand: 'Brand',
      designer: 'Designer',
      season: 'Season',
      style: 'Style',
      priceUnit: 'Price unit',
      spotsTotal: 'Total quantity',
      spotsAvailable: 'Available',
      status: 'Status',
      imageUrl: 'Image URL',
      highlights: 'Highlights',
      includes: 'Included',
    },
    priceUnits: {
      perItem: 'SEK/item',
      perSet: 'SEK/set',
      perDress: 'SEK/dress',
      perSuit: 'SEK/suit',
    },
    statuses: {
      active: 'Active',
      soldOut: 'Sold out',
      draft: 'Draft',
    },
    highlightPlaceholder: 'Highlight {n}',
    includesPlaceholder: 'Included item {n}',
    featured: 'Featured collection',
  },
  fortnox: {
    title: 'Bookkeeping in Fortnox',
    intro: 'Each day becomes one voucher for incoming payments, one for payouts to sellers and one for refunds. Nothing is booked twice. Stripe fees and Stripe payouts to the bank are booked as before, outside Qvestium.',
    notConfigured: 'Fortnox is not configured on the server (FORTNOX_CLIENT_ID, FORTNOX_CLIENT_SECRET, FORTNOX_REDIRECT_URI and SETTINGS_ENCRYPTION_KEY).',
    connect: 'Connect Fortnox',
    connecting: 'Opening Fortnox…',
    connectHint: 'If you are already signed in to Fortnox the connection may be refused — open Qvestium in a private window and connect again.',
    connected: 'Connected to {company}',
    connectedUnknown: 'Connected to Fortnox',
    disconnect: 'Disconnect',
    confirmDisconnect: 'Disconnect Fortnox? Nothing already booked is affected.',
    resultOk: 'Fortnox is connected.',
    resultDenied: 'The connection was cancelled in Fortnox.',
    resultFailed: 'Couldn\'t connect Fortnox. Try again.',
    resultInvalidState: 'The connection took too long or was invalid. Try again.',
    settings: 'Accounts and voucher series',
    series: 'Voucher series',
    save: 'Save',
    saved: 'Saved.',
    resetDefaults: 'Default accounts',
    check: 'Check against Fortnox',
    checkOk: 'The series and all accounts exist in Fortnox.',
    accounts: {
      cash: 'Stripe balance (receivable)',
      sellerLiability: 'Owed to sellers/lenders',
      insuranceLiability: 'Owed to Omocom (insurance)',
      protectionFeeRevenue: 'Revenue buyer protection (25 %)',
      commissionRevenue: 'Revenue rental commission (25 %)',
      featureRevenue: 'Revenue featured listings (25 %)',
      shippingRevenue: 'Revenue shipping swaps/returns (25 %)',
      outputVat: 'Output VAT 25 %',
    },
    autoBook: 'Book automatically every night',
    autoBookHint: 'Yesterday\'s events are booked after 03:00 and a summary is emailed to admins. You book older events yourself here.',
    autoBookLast: 'Last booked day: {day}',
    autoBookOn: 'Nightly bookkeeping is on.',
    autoBookOff: 'Nightly bookkeeping is off.',
    backfill: 'Load earlier deals',
    backfillDone: { one: '{count} earlier event loaded.', other: '{count} earlier events loaded.' },
    backfillNote: { one: '{count} event in the period has an estimated date (loaded afterwards) — check before booking.', other: '{count} events in the period have estimated dates (loaded afterwards) — check before booking.' },
    period: 'Period: {from} – {to}',
    toBook: 'To book',
    nothingToBook: 'Nothing to book in the period.',
    book: { one: 'Book {count} voucher in Fortnox', other: 'Book {count} vouchers in Fortnox' },
    booking: 'Booking…',
    confirmBook: { one: 'Book {count} voucher in Fortnox? This can\'t be undone here.', other: 'Book {count} vouchers in Fortnox? This can\'t be undone here.' },
    bookedResult: { one: '{count} voucher booked.', other: '{count} vouchers booked.' },
    errorsResult: { one: '{count} failed — see below.', other: '{count} failed — see below.' },
    booked: 'Booked in the period',
    retry: 'Try again',
    category: { in: 'Incoming payments', out: 'Payouts', refund: 'Refunds' },
    status: { booking: 'Booking…', booked: 'Booked', error: 'Error' },
    account: 'Account',
    text: 'Text',
    debit: 'Debit',
    credit: 'Credit',
    showRows: 'Show rows',
    hideRows: 'Hide rows',
    loadFailed: 'Couldn\'t load the bookkeeping',
  },
  bgRemoval: {
    remove: 'Remove background',
    useStored: 'Use cut-out',
    showOriginal: 'Show original',
    title: 'Remove background',
    before: 'Original',
    after: 'Cut-out',
    downloading: 'Downloading the image model the first time… {percent} %',
    downloadingStart: 'Downloading the image model the first time…',
    working: 'Removing the background…',
    hint: 'Everything happens in your browser. The original photo is always kept and you can switch back any time.',
    useCutout: 'Use cut-out',
    keepOriginal: 'Keep original',
    saving: 'Saving…',
    error: 'Couldn\'t remove the background right now. The original photo is used.',
    timeout: 'Removing the background took too long. The original photo is used.',
    saveError: 'Couldn\'t save the cut-out. The original photo is used.',
    close: 'Close',
  },
  aiImageGenerator: {
    partialErrors: 'Some variants couldn\'t be generated: {errors}',
    failed: 'AI image generation failed',
    intro: 'Create an image of the item on a model',
    generating: 'Generating... (may take ~30s)',
    generate: 'Generate AI images',
    modelPhotoLabel: 'Model photo for "on model":',
    aiModelTitle: 'AI-generated model',
    aiModel: 'AI model',
    addSelected: { one: 'Add {count} selected image to the gallery', other: 'Add {count} selected images to the gallery' },
  },
  addProductModal: {
    imageUploadFailed: 'Image upload failed',
    nameRequired: 'Name is required',
    invalidPrice: 'Enter a valid price',
    title: 'New item / accessory',
    nameLabel: 'Name *',
    namePlaceholder: 'E.g. Silk blouse summer 2025',
    descriptionPlaceholder: 'Short description of the item...',
    purchasePriceLabel: 'Purchase price (SEK) *',
    rentalPriceLabel: 'Monthly rental price, per commitment period',
    perMonthPlaceholder: 'SEK/mo',
    prefillHint: 'Pre-filled with a suggestion when you enter the purchase price — feel free to change them.',
    sortOrder: 'Sort order',
    published: 'Published',
    draftHint: '(otherwise a draft, visible to admins only)',
    image: 'Image',
    imagePlaceholder: 'https://... or upload below',
    previewAlt: 'Preview',
    galleryCount: 'Image gallery ({count})',
    inStock: 'In stock',
    create: 'Create item',
  },
  editProductModal: {
    imageUploadFailed: 'Image upload failed',
    galleryUploadFailed: 'Gallery upload failed',
    title: 'Edit product',
    nameLabel: 'Name *',
    purchasePriceLabel: 'Purchase price (SEK)',
    published: 'Published',
    rentalPriceLabel: 'Monthly rental price, per commitment period',
    perMonthPlaceholder: 'SEK/mo',
    conditionLabel: 'Condition (1 = Worn · 5 = Unused)',
    mainImage: 'Main image',
    imagePlaceholder: 'URL or upload',
    previewAlt: 'Preview',
    galleryCount: { one: 'Image gallery ({count} image)', other: 'Image gallery ({count} images)' },
    addImages: '+ Add images',
    inStock: 'In stock',
  },
  announcementBar: {
    text: 'Make your wardrobe smart! Easily swap, rent out, sell, buy or rent great-looking pieces.',
  },
  promoBanner: {
    title: 'Make your wardrobe limitless',
    body: 'Swap, rent and sell clothes easily with friends and other users',
  },
  mapErrorBoundary: {
    failed: 'The map couldn\'t be displayed',
  },
  mapFallback: {
    loading: 'Loading map…',
  },
  areaPickerModal: {
    geolocationUnsupported: 'Your browser can\'t provide your location',
    locationFailed: 'Couldn\'t get your location',
    title: 'Choose area',
    hint: 'Click on the map where you want to find items.',
    within: 'Within',
    radiusKm: '{km} km',
    locating: 'Locating…',
    myPosition: 'My location',
    dotsHint: 'The dots show roughly where sellers are located.',
    sellersInArea: { one: '{count} seller in this area.', other: '{count} sellers in this area.' },
    showAllSweden: 'Show all of Sweden',
    showItemsHere: 'Show items here',
  },
  areaFilterChip: {
    withinRadius: 'Within {radius} km of selected location',
    clearAreaFilter: 'Remove area filter',
  },
  navSearchInput: {
    placeholder: 'Search for brands, items...',
    pickAreaOnMap: 'Choose an area on the map',
  },
  allTabs: {
    items: 'Clothing',
    accessories: 'Accessories',
    upcomingDrops: 'Upcoming drops',
    myWardrobe: 'My wardrobe',
  },
  adminTab: {
    label: 'Admin',
  },
  tabBar: {
    more: 'More',
  },
  returnStatusLabels: {
    ordered: 'Ordered',
    activated: 'Activated',
    shipped: 'Shipped',
    received: 'Received',
  },
  swapStatusLabels: {
    pending: 'Pending',
    accepted: 'Accepted',
    awaitingHandover: 'Awaiting handover',
    completed: 'Completed',
    rejected: 'Declined',
    cancelled: 'Cancelled',
    disputed: 'Paused – support is looking into it',
  },
  offerStatusLabels: {
    pending: 'Pending',
    accepted: 'Accepted — awaiting payment',
    paid: 'Paid',
    rejected: 'Declined',
    cancelled: 'Cancelled',
  },
  transactionStatusLabels: {
    awaitingPayment: 'Awaiting payment',
    pending: 'Awaiting shipment',
    shipped: 'In transit',
    delivered: 'Delivered',
    returnRequested: 'Return requested',
    returnShipped: 'Return shipped',
    completed: 'Completed',
    returnReceived: 'Returned',
    cancelled: 'Cancelled',
    refunded: 'Duplicate payment — refunded',
  },
  getWardrobeGuideMessage: {
    newSwaps: { one: 'You have {count} new swap proposal to respond to.', other: 'You have {count} new swap proposals to respond to.' },
    showSwaps: 'View swap proposals',
    newOffers: { one: 'You have {count} new offer on your items to respond to.', other: 'You have {count} new offers on your items to respond to.' },
    showOffers: 'View offers',
    pendingSales: { one: '{count} item is waiting to be shipped.', other: '{count} items are waiting to be shipped.' },
    showToShip: 'See what needs shipping',
    emptyWardrobe: 'Your wardrobe is empty — want to upload your first item? Tap the image icon below and I\'ll take care of the rest!',
    nothingListed: { one: 'You have {count} item in your wardrobe but nothing listed yet. Want to list something for sale or rent?', other: 'You have {count} items in your wardrobe but nothing listed yet. Want to list something for sale or rent?' },
    showMyItems: 'View my items',
    suggestDrop: 'Nice, you\'ve got items listed! Want to create a drop and share it on Instagram/TikTok?',
    createDrop: 'Create drop',
    allGood: 'Everything looks good in your wardrobe right now! 👍',
  },
  createWardrobeItemFromImage: {
    uploading: 'Uploading the image...',
    analyzing: 'Analysing the item...',
    creating: 'Creating the item...',
  },
  qChat: {
    welcomeNamed: 'Hi {name}! I\'m Q 👋 Upload photos of items you want to add to your wardrobe (tap the image icon) and I\'ll create them for you automatically — with AI images, a description and suggested prices. Or ask me anything!\n\n{greeting}',
    welcome: 'Hi! I\'m Q 👋 Upload photos of items you want to add to your wardrobe (tap the image icon) and I\'ll create them for you automatically — with AI images, a description and suggested prices. Or ask me anything!\n\n{greeting}',
    pickItemsForDrop: 'Choose items for the drop',
    importNow: 'Import now',
    replyFailed: 'I couldn\'t reply just now — please try again in a moment.',
    uploadingAndAnalyzing: 'Uploading and analysing the item...',
    itemCreated: 'Done! I created **{name}** for you as a draft — {description}\n\nSuggestion: SEK {salePrice} to sell, or SEK {rentPrice}/month to rent out. It isn\'t published yet — find it under "My items" and switch on rental/sale when you\'re happy with it!',
    createFailed: 'Something went wrong while creating the item. Please try again!',
    importProgress: 'Importing {done}/{total} items...',
    importFailed: 'The import failed.',
    importDone: { one: 'Done! Imported {count} item from Vinted. Find it under "My items" in your wardrobe!', other: 'Done! Imported {count} items from Vinted. Find them under "My items" in your wardrobe!' },
    importDoneCapped: { one: 'Done! Imported {count} item from Vinted (limited to {total} items per run — run the import again for more). Find it under "My items" in your wardrobe!', other: 'Done! Imported {count} items from Vinted (limited to {total} items per run — run the import again for more). Find them under "My items" in your wardrobe!' },
    importLostContact: 'Lost contact with the import — check your wardrobe to see how far it got.',
    importStartFailed: 'Couldn\'t start the import.',
    subtitle: 'Your AI wardrobe assistant',
    copy: 'Copy',
    draftNotPublished: 'Draft · not published',
    uploadImages: 'Upload photos of items',
    placeholderBusy: 'Q is working on your photos...',
    placeholder: 'Ask Q...',
  },
  bulkUploadModal: {
    starting: 'Starting...',
    title: 'Upload multiple items',
    subtitle: 'Pick as many photos as you like — Q analyses each item automatically and creates a draft with AI images, a description and suggested prices.',
    chooseImages: 'Choose photos',
    progress: '{done} of {total} done',
    waiting: 'Waiting...',
    doneClose: 'Done, close',
    addMoreImages: '+ Add more photos',
  },
  returnVerdicts: {
    destroyed: 'Completely ruined',
    destroyedHint: 'Replacement value minus the rent you received',
    heavilyDegraded: 'Badly damaged',
    heavilyDegradedHint: '50% of the replacement value',
    wear: 'Wear or stains',
    wearHint: '30% of the replacement value',
  },
  returnStatusLabel: {
    active: 'Time to return',
    awaitingReview: 'Awaiting condition review',
    approved: 'Condition approved',
    compensationProposed: 'Compensation proposed',
    compensationAgreed: 'Compensation agreed',
    escalated: 'Being handled by support',
    handedOver: 'Returned — awaiting confirmation',
    shippingBooked: 'Shipping booked',
    completed: 'Completed',
  },
  returnPhotoRow: {
    noImages: 'No photos',
  },
  itemReturnCard: {
    paymentStartFailed: 'Couldn\'t start the payment',
    uploadFailed: 'Upload failed',
    rentedFrom: 'Rented from {name}',
    borrowedFrom: 'Borrowed from {name}',
    rentedOutTo: 'Rented out to {name}',
    lentTo: 'Lent to {name}',
    dueBy: 'due back by {date}',
    inDays: { one: '(in {count} day)', other: '(in {count} days)' },
    overdue: '(overdue)',
    insuredViaOmocom: 'Insured through Omocom',
    reportDamageOmocom: 'Report damage to Omocom',
    reportDamageLater: 'if there\'s damage, you can also report it to Omocom once the return is complete',
    photoInstructions: 'Photograph the item (at least two photos, front and back) before you return it. The owner compares them with the listing photos and approves the condition.',
    dropoffAtOwner: 'Drop off at the owner\'s address',
    shipWithQrPrice: 'Ship with QR code (SEK {amount})',
    shipWithQrSoon: 'Ship with QR code — coming soon',
    removePhoto: 'Remove photo',
    uploadingShort: 'Uploading…',
    addPhoto: '+ Photo',
    sendingShort: 'Sending…',
    sendPhotosToOwner: 'Send photos to the owner',
    ownerActiveInfo: '{name} gets a reminder a week before and photographs the item when it\'s time. You approve the condition before it\'s sent back.',
    listingPhotos: 'Listing photos',
    returnPhotos: 'At return',
    awaitingReviewShip: '{name} is reviewing the photos. Hold off on shipping the item until the condition is approved.',
    awaitingReviewDropoff: '{name} is reviewing the photos. Hold off on dropping off the item until the condition is approved.',
    approveCondition: 'Approve condition',
    conditionNotOk: 'Condition isn\'t OK',
    replacementValuePlaceholder: 'Replacement value (SEK)',
    notePlaceholder: 'Describe what\'s wrong — shown to the renter',
    proposedCompensation: 'Proposed compensation: {amount}',
    proposeCompensation: 'Propose compensation',
    assessment: 'Assessment: {verdict} · compensation {amount}',
    replacementValue: '(replacement value SEK {amount})',
    borrowerDecidePrompt: 'Do you accept the proposal? If you can\'t agree, the case goes to Qvestium support.',
    lenderDecidePrompt: 'Do you stand by your proposal? Both of you need to accept — otherwise the case goes to support.',
    approve: 'Accept',
    escalateConfirm: 'The case will be sent to support for review. Continue?',
    disagreeToSupport: 'Don\'t agree — send to support',
    approvedWaitingFor: 'You\'ve accepted. Waiting for {name}.',
    payCompensationInfo: 'Pay the compensation to continue with the return.',
    payAmount: 'Pay SEK {amount}',
    waitingForPayment: 'Waiting for {name} to pay. The money will be paid out to your account.',
    escalatedInfo: 'You couldn\'t agree, or a step was delayed. Qvestium support has the case and will get in touch with you both.',
    payShippingInfo: 'Pay for shipping and you\'ll get a QR code to show at a Bring service point — no label needed.',
    payShipping: 'Pay shipping SEK {amount}',
    dropoffAt: 'Drop the item off at:',
    markHandedOver: 'Mark as returned',
    counterpartShips: '{name} pays for shipping and sends the item.',
    counterpartDropsOff: '{name} drops the item off at your address.',
    showQrInfo: 'Show the QR code at a Bring service point and hand in the parcel.',
    qrAlt: 'Shipping QR code',
    code: 'Code: {code}',
    shippingBookedInfo: 'Shipping is booked. Confirm once the parcel has arrived.',
    consignmentNumber: 'Tracking number {number}',
    waitingForReceipt: 'Waiting for {name} to confirm the item has been returned.',
    confirmReceived: 'Confirm I\'ve got the item back',
  },
  returnsSection: {
    title: 'Returns',
    completed: 'Completed ({count})',
  },
  minGarderobView: {
    confirmDeleteSelected: { one: 'Remove {count} item from your wardrobe?', other: 'Remove {count} items from your wardrobe?' },
    deleteSelectedFailed: 'Couldn\'t remove the items.',
    confirmCancelDrop: 'Cancel the drop? The items will be visible in the marketplace again right away (if they\'re listed).',
    confirmDeleteItem: 'Remove this item from your wardrobe?',
    loginTitle: 'Log in to see your wardrobe',
    loginSubtitle: 'All your bought, rented and own items in one place',
    tabs: {
      all: 'All',
      own: 'My own',
      bought: 'Bought',
      rented: 'Rented',
      forRent: 'For rent',
      forSale: 'For sale',
      liked: 'Liked',
      outfits: 'Outfits',
      drops: 'Drops',
      toShip: 'To ship',
      swaps: 'Swap proposals',
      offers: 'Offers',
      tracking: 'Tracking',
      returns: 'Returns',
      sales: 'Sales',
      reviews: 'Reviews',
    },
    title: 'My wardrobe',
    subtitle: 'Your bought, rented and own items',
    cancelDrop: 'Cancel drop',
    cancelDelete: 'Cancel removal',
    addDrop: '+ Drop',
    deleteItems: 'Remove items',
    addAccessories: '+ Add accessories',
    addItem: '+ Add item',
    dropSelectHint: 'Select the items to include in the drop (only your own items can be selected), then click {button} at the bottom.',
    deleteSelectHint: 'Select the items you want to remove (only your own items can be selected), then click {button} at the bottom.',
    next: 'Next',
    deleteSelected: 'Remove selected',
    deleting: 'Removing...',
    selectedCount: { one: '{n} item selected', other: '{n} items selected' },
    loadingWardrobe: 'Loading wardrobe...',
    liked: {
      emptyTitle: 'No liked items yet',
      emptyText: 'Items you like in the marketplace will show up here',
    },
    toShip: {
      title: 'To ship',
      empty: 'No items to ship right now',
      someoneBought: 'Someone bought your item! Time to ship it.',
      someoneRented: 'Someone rented your item! Time to ship it.',
      buyer: 'Buyer: {buyer}',
      awaitingBuyerConfirmation: 'Waiting for the buyer to confirm',
      confirmHandover: 'Confirm handover (pickup)',
      pickupHint: 'The buyer picks up the item — no shipping. Click once you\'ve met and the item has changed hands.',
      shipItem: 'Ship item',
    },
    itemFallback: 'Item',
    badgeSold: 'Sold',
    badgeRented: 'Rented out',
    commission: 'Commission',
    commissionRent: 'Commission (20%)',
    commissionDeducted: '-SEK {amount}',
    yourPayout: 'Your payout',
    yourReply: 'Your reply: "{message}"',
    reply: 'Reply: "{message}"',
    sendRejection: 'Send decline',
    trackingNumber: 'Tracking: {number}',
    swaps: {
      confirmCancel: 'Cancel the swap proposal?',
      receivedTitle: 'Received proposals',
      emptyReceived: 'No swap proposals received yet',
      yourItem: 'Your item',
      offeredBy: 'Offered by {user}',
      rejectPlaceholder: 'Message to the person who proposed the swap (optional)',
      acceptChooseDelivery: 'Accept / pickup or shipping...',
      sentTitle: 'Sent proposals',
      emptySent: 'You haven\'t sent any swap proposals yet',
      newReply: 'New reply',
      theirItem: 'Their item',
      youOffered: 'You offered',
      cancelProposal: 'Cancel proposal',
    },
    offers: {
      confirmCancel: 'Cancel the offer?',
      receivedTitle: 'Received offers',
      emptyReceived: 'No offers received yet',
      offerFrom: 'Offer from {name}',
      rejectPlaceholder: 'Message to the person who made the offer (optional)',
      accept: 'Accept',
      sentTitle: 'Sent offers',
      emptySent: 'You haven\'t made any offers yet',
      cancelOffer: 'Withdraw offer',
      payAmount: 'Pay SEK {amount}',
      payAmountPerMonth: 'Pay SEK {amount}/month',
    },
    tracking: {
      title: 'Active transactions',
      empty: 'No active transactions',
    },
    returns: {
      bagsTitle: 'Return bags',
      orderBag: 'Order a return bag',
      emptyBags: 'No return bags yet',
      stepOrdered: 'Ordered',
      stepActivated: 'Activated',
      stepShipped: 'Shipped',
      stepReceived: 'Received',
    },
    sales: {
      title: 'My sales',
      empty: 'No sales yet. List your items for rent or sale!',
      status: {
        pending: 'Awaiting delivery',
        shipped: 'Shipped',
        delivered: 'Delivered',
        returnRequested: 'Return requested',
        returnShipped: 'Return shipped',
        completed: 'Completed',
        returnReceived: 'Return received',
      },
    },
    drops: {
      title: 'My drops',
      empty: 'No drops created yet. Click "+ Drop" to schedule a drop.',
      statusLive: 'Live',
      statusCancelled: 'Cancelled',
      statusScheduled: 'Scheduled',
      releasedAt: 'Dropped {date}',
      releasesAt: 'Drops {date}',
    },
    empty: {
      own: 'No items of your own added yet',
      bought: 'No bought items',
      rented: 'No rented items',
      forRent: 'No items listed for rent',
      forSale: 'No items listed for sale',
      all: 'Your wardrobe is empty',
      addClothesHint: 'Add your clothes and choose whether to rent them out or sell them',
      browseHint: 'Browse the shop and start shopping!',
    },
  },
  stars: {
    ariaLabel: '{value} out of 5 stars',
  },
  starInput: {
    ariaLabel: 'Rating',
    starCount: { one: '{count} star', other: '{count} stars' },
  },
  reputationSummary: {
    buys: { one: '{count} purchase', other: '{count} purchases' },
    swaps: { one: '{count} swap', other: '{count} swaps' },
    rents: { one: '{count} rental', other: '{count} rentals' },
    reviewCount: { one: '({count} review)', other: '({count} reviews)' },
    noRatings: 'No reviews yet',
    memberSince: 'Member since {date}',
  },
  pendingReviewCard: {
    chooseRating: 'Choose 1–5 stars',
    withCounterparty: 'with {name} · {date}',
    howDidItGo: 'How did the deal with {name} go?',
    commentPlaceholder: 'Comment (optional) — shown on their profile',
    submit: 'Submit review',
  },
  reviewsSection: {
    toReviewTitle: 'To review',
    toReviewText: 'Rate the people you\'ve bought from, swapped or rented with. Your rating and comment are shown under their name.',
    emptyPending: 'No deals to review right now',
    yourReputationTitle: 'Your reputation',
    noReviewsReceived: 'You haven\'t received any reviews yet.',
  },
  sellerBadge: {
    soldBy: 'Sold by',
  },
  wardrobeItemDetailPage: {
    errorUpload: 'Couldn\'t upload the images',
    errorSetPrimary: 'Couldn\'t change the main image',
    primaryImage: 'Main image',
    makePrimary: 'Set as main image',
    uploading: 'Uploading...',
    addMoreImages: '+ Add more images',
    addImages: '+ Add images',
    sizeLabel: 'Size {size}',
    sourceOwn: 'My own item',
    sourceBought: 'Bought',
    sourceRented: 'Rented',
    listedFor: 'Listed for',
    openForSwap: 'Open to swaps',
    rentalChooseCommitment: 'Rental — choose a commitment period',
    buyerProtectionTotal: '+ Buyer Protection SEK {fee} = {total}',
    buyerProtectionInfo: 'Buyer Protection holds the payment until you\'ve received the item, and refunds you if it doesn\'t match the listing.',
    rentalCommissionInfo: 'Qvestium takes a 20% commission from the owner on rentals.',
    replacementValue: 'Replacement value if damaged: SEK {amount}.',
    rent: 'Rent',
    buyFor: 'Buy for SEK {amount}',
    messageSeller: 'Message the seller',
    proposeSwap: 'Propose a swap',
    proposeOffer: 'Make an offer to buy/rent',
    swapProposalSent: 'Swap proposal sent! The owner will be notified.',
    offerSent: 'Offer sent! The seller will be notified.',
    reportListing: 'Report listing',
  },
  wardrobeCard: {
    sourceOwn: 'Mine',
    sourceBought: 'Bought',
    sourceRented: 'Rented',
    deliveryStatus: {
      pending: 'Awaiting delivery',
      shipped: 'In transit',
      delivered: 'Delivered',
      returnRequested: 'Return requested',
      returnShipped: 'Return shipped',
      completed: 'Completed',
      returnReceived: 'Returned',
    },
    sold: 'Sold',
    inDrop: '🔒 In the drop "{name}" — releases {date}',
    sizeShort: 'Size {size}',
    tracking: 'Tracking: {number}',
    openForSwap: 'Open to swaps',
    toggleRent: 'For rent',
    toggleSale: 'For sale',
    confirmReceived: 'Confirm received',
    waitingForSeller: 'Waiting for seller',
    confirmPickedUp: 'Confirm pickup',
    returnItem: 'Return',
  },
  sellerWardrobeCard: {
    sold: 'Sold',
    sizeShort: 'Size {size}',
    openForSwap: 'Open to swaps',
    notListed: 'Not listed',
  },
  sellerProfileView: {
    loadingProfile: 'Loading profile...',
    approximateArea: 'Approximate area',
    sendMessage: 'Send message',
    reviewsHeading: 'Reviews ({count})',
    wardrobeHeading: 'Wardrobe ({count})',
    emptyWardrobe: 'No items added yet',
  },
  dropWizardModal: {
    errorNameRequired: 'Enter a name for the drop',
    errorReleaseRequired: 'Enter a date and time for the release',
    errorReleaseInPast: 'The release date must be in the future',
    createDrop: 'Create drop',
    creatingDrop: 'Creating drop...',
    itemsSelected: { one: '{count} item selected', other: '{count} items selected' },
    nameLabel: 'Drop name *',
    namePlaceholder: 'E.g. Autumn collection',
    releaseLabel: 'Release (date & time) *',
  },
  dropAnimationPreview: {
    presents: 'presents',
    releases: 'Drops',
  },
  dropShareBar: {
    copied: 'Copied!',
    copyLink: 'Copy link',
    shareNative: 'Share to Instagram / TikTok and more',
    shareHint: 'Open the link on your phone to share straight to Instagram/TikTok, or copy the link above.',
  },
  dropSharePanel: {
    shareText: '{name} — drops {date} on Qvestium',
    shareDrop: 'Share the drop',
  },
  dropVideoRecorder: {
    errorCanvasUnsupported: 'Canvas isn\'t supported',
    presents: 'PRESENTS',
    releases: 'Drops',
    errorEmptyFile: 'The video recording came out empty — this can be caused by the browser or by the tab being in the background. Try again with the tab active in the foreground, or share the link above instead.',
    errorFailed: 'Video recording failed',
    title: 'Video teaser to post',
    unsupported: 'Video recording isn\'t supported in this browser. Copy the link above and share it manually.',
    recording: 'Recording... (about 8 sec)',
    record: 'Record video',
    download: 'Download video',
    shareVideo: 'Share video',
  },
  transactionTrackingCard: {
    steps: {
      pending: 'Ordered',
      shipped: 'Shipped',
      delivered: 'Delivered',
      returnRequested: 'Return requested',
      returnShipped: 'Return shipped',
      completed: 'Completed',
    },
    enterTrackingNumber: 'Enter a tracking number',
    itemFallback: 'Item',
    yourPayout: 'Your payout',
    deliveryTracking: 'Delivery tracking: {number}',
    returnTracking: 'Return tracking: {number}',
    waitingForBuyer: 'Waiting for the buyer',
    confirmHandedOver: 'Confirm handed over (pickup)',
    trackingPlaceholder: 'Tracking number...',
    shipToBuyer: 'Ship to buyer',
    waitingForSeller: 'Waiting for the seller',
    confirmPickedUp: 'Confirm picked up',
    confirmReceived: 'Confirm received',
    requestReturn: 'Request return',
    returnTrackingPlaceholder: 'Return tracking number...',
    shipReturn: 'Ship return',
    confirmReturnReceived: 'Confirm return received',
    claimWindow: 'Check the item. The seller is paid on {date} unless you report a problem.',
    reportProblem: 'Report a problem',
    reportPlaceholder: 'What is wrong? E.g. the item has not arrived, is damaged or does not match the listing.',
    sendReport: 'Send report',
    reportSent: 'Report sent (case {number}). The seller is not paid while we look into it.',
    problemReported: 'You have reported a problem. Qvestium support is looking into it and will get back to you.',
    sellerPayoutDue: 'Payout on {date}, unless the buyer reports a problem.',
    sellerProblemReported: 'The buyer has reported a problem. Your payout is paused while support looks into it.',
    rentPlan: 'Rent: {paid} of {total} months paid',
    rentNext: 'next charge {date}',
    rentPaidOut: 'Paid out for {paid} of {total} months',
    rentActionRequired: 'Month {month} ({amount} kr) could not be charged to your card. Pay within 7 days and the rental continues as usual.',
    rentSellerWaiting: 'The renter has not paid month {month} yet — we have sent a reminder and will follow up if it takes longer.',
    payRent: 'Pay {amount} kr',
    cancelPurchase: 'Cancel purchase',
    cancelConfirm: 'Cancel the purchase of {item}? You get the full amount ({amount} kr) back to your card within 5–10 banking days.',
    cancelled: 'The purchase is cancelled. {amount} kr will be refunded to your card.',
    cancelRequestConfirm: 'Ask the seller to cancel the purchase of {item}? The seller confirms that the item has not been handed over. If the seller does not reply within 72 hours, the purchase is cancelled and you get {amount} kr back.',
    cancelRequested: 'The seller has your request. You will hear back within 72 hours.',
    cancelRequestPending: 'You have asked the seller to cancel. If the seller does not reply within 72 hours, it is cancelled automatically.',
    cancelObjected: 'The seller says the item has already been handed over. Support is looking into it.',
    cancelObjectedBySeller: 'You have said the item was handed over. Support is looking into it and will get back to you.',
    sellerCancelRequest: 'The buyer wants to cancel. Have you not handed the item over? Approve, and the buyer gets the money back. Without a reply within 72 hours, the purchase is cancelled.',
    approveCancel: 'Approve',
    objectCancel: 'Item handed over',
    approveCancelConfirm: 'Cancel the purchase? The buyer gets the money back and the item is available in your wardrobe again.',
    objectCancelPrompt: 'Briefly say when and where the item was handed over. Support will look into it.',
    cancelApproved: 'The purchase is cancelled and the buyer gets the money back.',
    cancelObjectedSeller: 'Support has the case ({ticket}) and will get back to you.',
  },
  shipToCustomerModal: {
    // Avsiktligt svenska: QR-koden läses av ombudet hos Bring i Sverige.
    qrReference: 'Qvestium-order\nOrder: {order}\nPlagg: {item}\nKöpare: {buyer}',
    itemFallback: 'Item',
    enterTrackingNumber: 'Enter a tracking number',
    registerFailed: 'Couldn\'t register the shipment',
    title: 'Ship item',
    buyer: 'Buyer: {buyer}',
    payout: 'Payout: SEK {amount}',
    shipmentRegistered: 'Shipment registered!',
    buyerNotified: 'The buyer has been notified.',
    referenceInfo: 'Show this code when you drop off the item at a Bring service point — it\'s a reference to your order, not a shipping label. The service point will give you a tracking number, which you enter in the next step.',
    orderRef: 'Order {order}',
    droppedOffContinue: 'I\'ve dropped off the item, continue',
    trackingIntro: 'Enter the carrier and tracking number for the delivery to the buyer:',
    trackingPlaceholder: 'Tracking number',
    autoPayoutInfo: 'With the carrier specified, we can track the delivery automatically and pay you out as soon as the parcel is delivered.',
    registering: 'Registering...',
    confirmShipment: 'Confirm shipment',
  },
  addWardrobeItemModal: {
    imageUploadFailed: 'Image upload failed',
    enterName: 'Enter a name',
    title: 'Add item',
    nameRequired: 'Name *',
    namePlaceholder: 'E.g. Black wool coat',
    descriptionPlaceholder: 'Material, condition, details...',
    sizePlaceholder: 'M, 38, ...',
    conditionRange: 'Condition (1-5)',
    weight: 'Weight (for shipping price)',
    image: 'Image',
    imageUrlPlaceholder: 'URL or upload',
    gallery: 'Image gallery ({count})',
    listQuestion: 'Do you want to list the item?',
    sell: 'Sell',
    currency: 'SEK',
    rentOut: 'Rent out',
    perMonthPlaceholder: 'SEK/mo',
    prefilledHint: 'Prefilled with a suggestion based on the sale price — feel free to change.',
    replacementValue: 'Replacement value (SEK)',
    replacementValuePlaceholder: 'What the item is worth',
    replacementValueHint: 'Shown to the renter. Used to calculate compensation if the item is ruined or damaged (100%, 50% or 30%).',
    feeInfo: 'Sales: no commission — you get the full price (the buyer pays for Buyer Protection). Rentals: Qvestium takes a 20% commission.',
    submit: 'Add to wardrobe',
  },
  swapProposalDetailModal: {
    title: 'Swap proposal',
    yourItem: 'Your item',
    theirItem: 'Their item',
    offeredBy: 'Offered by {user}',
    youOffered: 'You offered',
    message: 'Message',
    reply: 'Reply',
    sentAt: 'Sent {date}',
    updatedAt: 'Updated {date}',
    rejectPlaceholder: 'Message to the person who proposed the swap (optional)',
    sendRejection: 'Send decline',
    handoverQuestion: 'How do you want to hand over your item?',
    pickup: 'Pickup',
    shippingWithCost: 'Shipping (SEK {amount})',
    accept: 'Accept',
    cancelProposal: 'Cancel proposal',
    meetingPlace: 'Meeting place (owner\'s address)',
    myItemLabel: 'Your item ({method}):',
    theirItemLabel: 'Their item ({method}):',
    methodShipping: 'shipping',
    methodPickup: 'pickup',
    shippingPaid: 'Shipping paid ✓',
    awaitingShippingPayment: 'Waiting for you to pay for shipping (SEK {amount})',
    awaitingShippingAndProtectionPayment: 'Waiting for you to pay for shipping and Buyer Protection (SEK {amount})',
    youConfirmedHandover: 'You\'ve confirmed the handover ✓',
    awaitingYourHandover: 'Waiting for you to confirm the handover',
    handledByOther: 'Handled by the other party',
    otherConfirmed: 'The other party has confirmed ✓',
    waitingForOther: 'Waiting for the other party',
    payShipping: 'Pay for shipping (SEK {amount})',
    payShippingAndProtection: 'Pay for shipping and Buyer Protection (SEK {amount})',
    confirmHandover: 'Confirm the handover has taken place',
    confirmReceived: 'I have received the items',
    youConfirmedReceived: 'You have confirmed that the items arrived.',
    reportProblem: 'Report a problem',
    reportPlaceholder: 'What is wrong? The swap is paused while support looks into it.',
    sendReport: 'Send report',
    reportSent: 'Report sent. The swap is paused until support gets in touch.',
  },
  proposeSwapModal: {
    loadFailed: 'Couldn\'t load your wardrobe',
    selectAtLeastOne: 'Select at least one item to offer',
    title: 'Propose swap',
    intro: 'Choose the items from your wardrobe you want to offer in exchange for {item}.',
    loadingWardrobe: 'Loading your wardrobe...',
    noItems: 'You don\'t have any items available to offer.',
    period: 'Swap period',
    permanentInfo: 'The items change owners permanently if the proposal is accepted.',
    temporaryInfo: { one: 'A temporary swap for {count} month — agree with the owner to swap back when the period ends.', other: 'A temporary swap for {count} months — agree with the owner to swap back when the period ends.' },
    handoverQuestion: 'How do you want to hand over your items?',
    pickup: 'Pickup',
    shippingWithCost: 'Shipping (SEK {amount})',
    shippingInfo: 'If you choose shipping, you pay for your own — the owner chooses their method when they reply.',
    protectionInfo: '+ Buyer Protection SEK {amount} (5% of the item\'s value + SEK 7.50). The swap is only completed once both items have arrived. If you pick up the items yourselves, the swap is free.',
    messageLabel: 'Message (optional)',
    messagePlaceholder: 'Write a message to the owner...',
    sending: 'Sending proposal...',
    submit: 'Propose swap ({count} selected)',
  },
  proposeOfferModal: {
    invalidOffer: 'Enter an offer greater than 0',
    title: 'Propose purchase/rental',
    intro: '{item} isn\'t listed right now — make the seller an offer.',
    rent: 'Rent',
    yourOfferBuy: 'Your offer (SEK)',
    yourOfferRent: 'Your offer (SEK/mo)',
    pricePlaceholder: 'E.g. 500',
    messageLabel: 'Message (optional)',
    messagePlaceholder: 'Write a message to the seller...',
    sending: 'Sending offer...',
    submit: 'Send offer',
  },
  editWardrobeItemModal: {
    imageUploadFailed: 'Image upload failed',
    galleryUploadFailed: 'Gallery upload failed',
    deleteConfirm: 'Remove the item from your wardrobe?',
    title: 'Edit item',
    name: 'Name',
    condition: 'Condition',
    weight: 'Weight (for shipping price)',
    image: 'Image',
    gallery: { one: 'Image gallery ({count} image)', other: 'Image gallery ({count} images)' },
    addImages: '+ Add images',
    listing: 'Listing',
    sell: 'Sell',
    currency: 'SEK',
    rentOut: 'Rent out',
    perMonthPlaceholder: 'SEK/mo',
    replacementValue: 'Replacement value (SEK)',
    replacementValuePlaceholder: 'What the item is worth',
    replacementValueHint: 'Shown to the renter. Used to calculate compensation if the item is ruined or damaged (100%, 50% or 30%).',
    feeInfo: 'Sales: no commission — you get the full price (the buyer pays for Buyer Protection). Rentals: Qvestium takes a 20% commission.',
  },
  returnFlowModal: {
    returnRequested: 'Return requested! A return bag has been ordered.',
    bagOrdered: 'Return bag ordered! Code: {code}',
    enterQrCode: 'Enter the QR code',
    bagActivated: 'Return bag activated! Put the item inside and drop it off.',
    codeNotFound: 'Code not found',
    enterTrackingNumber: 'Enter the tracking number',
    transactionMissing: 'Transaction not found',
    returnShipped: 'Return sent! The seller has been notified.',
    title: 'Return item',
    rented: 'Rented',
    bought: 'Bought',
    trackingInstructions: 'Put the item in the bag, drop it off at a Bring service point and enter the tracking number:',
    trackingPlaceholder: 'Tracking number',
    later: 'Later',
    confirmReturnShipment: 'Confirm return shipment',
    requestingReturn: 'Requesting return...',
    requestReturn: 'Request return (return bag ordered automatically)',
    requestReturnHint: 'A return bag is ordered automatically. You can also use your own bag.',
    orderBag: 'Order return bag',
    haveBagScan: 'I already have a bag — scan QR',
    orderBagHint: 'The return bag is sent to your home free of charge. Put the item inside and drop it off.',
    orderBagInfo: 'A return bag will be sent to your registered address. Put the item inside, attach the label and drop it off at your nearest Bring service point.',
    ordering: 'Ordering...',
    enterBagCode: 'Enter the code on your return bag (e.g. QB-A3X7YZ):',
    activating: 'Activating...',
    activateBag: 'Activate bag',
  },
  scanActivateButton: {
    codeNotFound: 'Code not found',
    scanOrEnterCode: 'Scan QR / Enter code',
    activate: 'Activate',
  },
  shipBagForm: {
    couldNotRegister: 'Couldn\'t register shipment',
    trackingPlaceholder: 'Enter tracking number...',
    markShipped: 'Mark as shipped',
  },
  publicDropPage: {
    loading: 'Loading drop...',
    unavailable: 'This drop is no longer available.',
    toQvestium: 'Go to Qvestium',
    liveNow: 'Out now',
    releases: 'Drops {date}',
    days: 'Days',
    hours: 'Hrs',
    minutes: 'Min',
    seconds: 'Sec',
    seeInStore: 'See the drop in the shop',
  },
  app: {
    listingGone: 'This listing no longer exists',
    openListingFailed: 'Couldn\'t open the listing',
    confirmDeleteCollection: 'Delete this collection?',
    deleteCollectionFailed: 'Couldn\'t delete the collection',
    confirmDeleteProduct: 'Delete this product?',
    deleteProductFailed: 'Couldn\'t delete the product',
    loadingApp: 'Loading Qvestium...',
    articleCount: { one: '{count} item', other: '{count} items' },
    tryClearFilter: 'Try clearing a filter',
    loginToSeeMessages: 'Log in to see your messages.',
    loginToSeeProfile: 'Log in to see your profile',
    nav: {
      search: 'Search',
      myProfile: 'My profile',
      profileFallback: 'Profile',
      logOut: 'Log out',
      messages: 'Messages',
      messagesUnread: { one: 'Messages, {count} unread', other: 'Messages, {count} unread' },
    },
    drops: {
      title: 'Upcoming drops',
      subtitle: 'Scheduled drops from Qvestium sellers',
      loading: 'Loading drops...',
      emptyTitle: 'No drops right now',
      emptyHint: 'Stay tuned — new drops will appear here when sellers schedule them.',
      badgeLive: 'Live',
      badgeSoon: 'Soon',
      released: 'Dropped {date}',
      releases: 'Drops {date}',
    },
    plagg: {
      title: 'Clothing',
      emptyInArea: 'No items in this area',
      emptyNone: 'No items listed yet',
      emptyNoMatch: 'No items match your filters',
      tryLargerArea: 'Try a larger area',
      beFirst: 'Be the first — upload your item in My wardrobe!',
    },
    accessoarer: {
      title: 'Accessories',
      subtitle: 'Accessories, shoes and bags listed by other members',
      emptyNone: 'No accessories listed yet',
      emptyNoMatch: 'No accessories match your filters',
      beFirst: 'Be the first — upload your accessory in My wardrobe!',
    },
    footer: {
      tagline: 'Qvestium makes your wardrobe smart. Upload your items and easily swap, rent out or sell them to others.',
      shop: 'Shop',
      contact: 'Contact',
      contactUs: 'Contact us',
      reportIllegal: 'Report illegal content',
      sustainability: 'Sustainability',
      sustainabilityText: 'Together we make fashion even more sustainable by sharing our wardrobes with each other.',
    },
    checkout: {
      successTitle: 'Payment complete!',
      successText: 'Thank you for your order. You\'ll receive a confirmation by email.',
      orderRef: 'Order: {id}...',
      continueShopping: 'Continue shopping',
      cancelTitle: 'Payment cancelled',
      cancelText: 'You haven\'t been charged. Your items are still in your bag.',
      backToStore: 'Back to the shop',
    },
  },
  // ── Slut komponenter ──
  values: {
    category: {
      Alla: 'All',
      Plagg: 'Clothing',
      Accessoarer: 'Accessories',
      Skor: 'Shoes',
      Väskor: 'Bags',
      Övrigt: 'Other',
      Kollektion: 'Collection',
    },
    collectionCategory: {
      Alla: 'All',
      Kvinna: 'Women',
      Man: 'Men',
      Barn: 'Kids',
      Unisex: 'Unisex',
    },
    style: {
      Alla: 'All',
      Klassisk: 'Classic',
      Modern: 'Modern',
      Streetwear: 'Streetwear',
      Formell: 'Formal',
      Casual: 'Casual',
      Bohemisk: 'Bohemian',
    },
    priceUnit: {
      'kr': 'SEK',
      'kr/mån': 'SEK/mo',
      'kr/plagg': 'SEK/item',
      'kr/set': 'SEK/set',
      'kr/klänning': 'SEK/dress',
      'kr/kostym': 'SEK/suit',
    },
    stylePref: {
      Minimalistisk: 'Minimalist',
      Maximalistisk: 'Maximalist',
      Vintage: 'Vintage',
      Bohemisk: 'Bohemian',
      Preppy: 'Preppy',
      Athleisure: 'Athleisure',
      'Business casual': 'Business casual',
      'Avant-garde': 'Avant-garde',
    },
  },
  marketing: {
    intro: 'Ad creatives, campaigns on Meta and TikTok, and what the ads lead to: visits, sign-ups and accounts that list an item within a week.',
    loadFailed: 'Could not load campaigns',
    newCampaign: 'New campaign',
    addLaunchTest: 'Add the test plan',
    create: 'Create',
    working: 'Working…',
    start: 'Start',
    pause: 'Pause',
    empty: {
      title: 'No campaign yet',
      body: 'Start with the launch test from the campaign proposal: three concepts with two openings each, SEK 6,000 over two weeks, with checklist and ad copy filled in.',
      cta: 'Add the test plan',
    },
    sections: { results: 'Results', creatives: 'Creatives', plan: 'Plan and budget', platforms: 'Meta and TikTok' },
    status: { draft: 'Draft', live: 'Live', paused: 'Paused', done: 'Finished', idea: 'Idea', production: 'In production', ready: 'Ready', stopped: 'Stopped' },
    kpi: {
      spend: 'Spent',
      ofBudget: 'of {budget} · {left} left',
      visits: 'Visits from ads',
      perVisit: '{cost} per visit',
      registrations: 'Sign-ups',
      unverified: '+ {count} who never confirmed their email',
      costPerRegistration: 'Cost per sign-up',
      activated: 'Activated within 7 days',
      activationRate: '{rate} of sign-ups · {pending} pending',
      costPerActivated: 'Cost per activated',
      requests: 'Requests',
      transactions: '{count} completed deals',
    },
    results: {
      sync: 'Fetch results',
      lastSync: 'Last fetched: {when}',
      neverSynced: 'No results fetched from Meta or TikTok yet.',
      notConnected: 'not connected',
      rowsSynced: '{count} rows',
      perConcept: 'By concept',
      perOpening: 'By opening',
      variant: 'Opening',
      concept: 'Concept',
      spend: 'Spent',
      impressions: 'Impressions',
      hookRate: 'Stay',
      hookRateHint: 'Share of impressions watched for at least 2 seconds. Compares openings — not the business goal.',
      clicks: 'Clicks',
      visits: 'Visits',
      visitsHint: 'Our own visits from the ad link / landing page views according to Meta',
      perVisit: 'SEK/visit',
      registrations: 'Sign-ups',
      perRegistration: 'SEK/sign-up',
      activated: 'Activated',
      activatedHint: 'Listed their first item within 7 days of signing up',
      requests: 'Requests',
      transactions: 'Deals',
      unknownVariant: 'Unknown variant',
      unknownVariantHint: 'Campaign traffic where utm_content is missing or does not match',
      total: 'Total',
      platformLine: '{spend} · {visits} visits · {regs} sign-ups · {cpr}/sign-up',
      howToRead: 'How to read the test.',
      decisionRule: 'Pick winners by action, not watch time. Cost per sign-up is the first shared metric; the share who list an item within 7 days shows quality. Only compare once variants have had reasonable exposure — zero sign-ups after a few clicks is not a loser. With few sign-ups the test is indicative, not statistically conclusive.',
    },
    chart: { spend: 'Spent per day', registrations: 'Sign-ups', label: 'Spend and sign-ups per day' },
    creatives: {
      intro: 'Each opening has its own ad link per platform. The link carries the campaign code {code}, so visitors can sign up directly, and utm_content decides which opening the sign-up counts towards. Use the same link in Ads Manager if you build the ad there.',
      add: 'New opening',
      none: 'No creatives yet.',
      concept: 'Concept',
      variantKey: 'Variant key (utm_content)',
      hook: 'Opening',
      adText: 'Ad copy',
      cta: 'CTA',
      landingPath: 'Landing page',
      desiredAction: 'Desired action:',
      showScript: 'Show script',
      hideScript: 'Hide script',
      noMedia: 'No video yet',
      uploadMedia: 'Upload video',
      replaceMedia: 'Replace video',
      uploading: 'Uploading…',
      mediaSpec: 'Vertical MP4, 1080 × 1920, max 200 MB',
      copyLink: 'Copy link',
      copied: 'Copied',
      adId: 'ad {id}',
      notPublished: 'not published',
      confirmStartAd: 'Start {variant} on {platform}? The ad starts showing and costing money if the campaign is live.',
      confirmDelete: 'Delete {variant}? Published ads stay on the platform.',
      delete: 'Delete',
    },
    plan: {
      name: 'Name',
      status: 'Status',
      budget: 'Total budget (SEK)',
      dailyBudget: 'Daily budget (SEK)',
      startDate: 'Start',
      endDate: 'End',
      audience: 'Audience',
      notes: 'Notes and decision rule',
      inviteCode: 'Campaign code in the ad links:',
      checklist: 'Checklist',
      noWhen: 'Other',
      when: 'When',
      newItem: 'New item',
      removeItem: 'Remove',
      delete: 'Delete campaign',
      confirmDelete: 'Delete {name} and all its creatives? Results on Meta and TikTok are not affected.',
    },
    platforms: {
      intro: 'Publish creates the campaign, ad group and one ad per ready opening on the platform — all paused, so nothing costs money. All openings go into the same ad group with the same audience (Sweden, 18+) and daily budget, as the test plan prescribes. Start once the videos have been reviewed.',
      connected: 'Connected',
      notConnected: 'Not connected',
      problem: 'Problem',
      account: 'Ad account: {account} ({currency})',
      currencyWarning: 'The ad account uses {currency}, not SEK — budgets and costs are in that currency.',
      setup: 'Add to Secret Manager and deploy the API:',
      metaSetup: 'Create a system user in Meta Business Manager with access to the ad account and page, and a token with ads_management and ads_read.',
      tiktokSetup: 'Create an app in TikTok for Business, authorise the ad account and get a long-lived token. The identity is the account the ads are shown from.',
      notPublished: 'Not published yet.',
      publishedIds: 'Published · campaign {campaign}',
      readyCount: '{count} ready openings to publish',
      publish: 'Publish paused',
      publishMore: 'Publish new openings',
      published: '{count} ads created paused on {platform}.',
      skippedImages: 'Skipped {variants} (TikTok requires video).',
      confirmStart: 'Start the campaign on {platform}? It starts showing immediately at {daily} per day (total budget {budget}).',
      started: 'The campaign is live on {platform}.',
      paused: 'The campaign is paused on {platform}.',
      openManager: 'Open {platform} Ads Manager',
      manualHint: 'Building ads directly in Ads Manager? Name the campaign so it contains {utm} and each ad so it contains the variant key (e.g. friends_a), and use the ad link from Creatives. Then the results land correctly here when you fetch them.',
    },
  },
};

export default en;
